import { FC, FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Box, Button, Link, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { submitDemoLead } from 'src/app/services/demoLead';
import { sendEditorMetric } from 'src/utils/editorMetrics';
import { privacyPolicyUrl } from 'src/utils/siteLinks';

type Status = 'idle' | 'sending' | 'done' | 'invalid' | 'rateLimited' | 'error';

const ERROR_KEYS: Partial<Record<Status, string>> = {
  invalid: 'demo.lead.errorInvalid',
  rateLimited: 'demo.lead.errorRateLimit',
  error: 'demo.lead.errorGeneric',
};

/**
 * Optional contact email at the end of the demo, under the next-step CTA.
 * Never required for anything: no other action waits on it, it opens no
 * dialog, and after the submit only this block changes (inline thank-you).
 * Copy: `demo.lead.*` (wording doc 26 Oct 2026, section 2). The button is the
 * consent action; the consent line sits directly above it.
 * The caller renders it only when the backend allows the UI locale
 * (useDemoLeadCaptureEnabled); the submit sends that same locale.
 */
export const DemoLeadCapture: FC = () => {
  const { t, i18n } = useTranslation();
  const baseId = useId();
  const headingId = `${baseId}-heading`;
  const consentId = `${baseId}-consent`;
  const hintId = `${baseId}-hint`;
  const errorId = `${baseId}-error`;
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const shownSent = useRef(false);

  useEffect(() => {
    if (shownSent.current) return;
    shownSent.current = true;
    // Cookieless funnel counter (#261); skipped under DNT/GPC.
    sendEditorMetric('lead_capture_shown');
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    const outcome = await submitDemoLead({
      email: email.trim(),
      locale: i18n.language,
      website,
    });
    setStatus(outcome === 'ok' ? 'done' : outcome);
  };

  if (status === 'done') {
    return (
      <Typography variant="body2" role="status" data-testid="demo-lead-done" sx={{ mt: 1.5 }}>
        {t('demo.lead.confirmation')}
      </Typography>
    );
  }

  const errorKey = ERROR_KEYS[status];
  const invalid = !!errorKey;
  return (
    <Box component="form" noValidate onSubmit={onSubmit} data-testid="demo-lead-capture" aria-labelledby={headingId} sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Typography id={headingId} variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {t('demo.lead.heading')}
      </Typography>
      <TextField
        type="email"
        size="small"
        fullWidth
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (errorKey) setStatus('idle');
        }}
        placeholder={t('demo.lead.placeholder') as string}
        autoComplete="email"
        error={invalid}
        helperText={errorKey ? t(errorKey) : t('demo.lead.hint')}
        FormHelperTextProps={{ id: invalid ? errorId : hintId, role: invalid ? 'alert' : undefined } as object}
        inputProps={{
          'aria-labelledby': headingId,
          'aria-describedby': `${invalid ? errorId : hintId} ${consentId}`,
          'aria-invalid': status === 'invalid' || undefined,
          'data-testid': 'demo-lead-email',
          maxLength: 254,
        }}
      />
      {/* Honeypot: hidden from people and assistive tech; bots fill it in. */}
      <Box aria-hidden="true" sx={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="demo-lead-website" />
        </label>
      </Box>
      <Typography id={consentId} variant="caption" component="p" color="text.secondary" sx={{ mt: 1, mb: 1 }} data-testid="demo-lead-consent">
        {t('demo.lead.consent')}{' '}
        <Link href={privacyPolicyUrl(i18n.language)} target="_blank" rel="noopener noreferrer">
          {t('demo.lead.privacyLink')}
        </Link>
      </Typography>
      <Button type="submit" size="small" variant="outlined" disabled={status === 'sending'} data-testid="demo-lead-submit">
        {t('demo.lead.submit')}
      </Button>
    </Box>
  );
};
